import _ from 'lodash';
import { call, put, takeLatest } from 'redux-saga/effects';
import { groupByDictUnique } from '../../../../common/utils';
import { createActionSetError, TErrorAction } from '../../actions/errorActions';
import { TAction } from '../../actions/types/action';
import { commonApiServiceAsync } from '../../services/commonApiService';
import { actions as messageCenterActions } from '../../messageCenter/modules';
import { TThreadState } from './types';

export type TListThreadPostsRequestParam = {
  slug?: string;
  id?: number;
};

export type TActions = {
  showLoadingSpinner(show: boolean): TAction<boolean>;
  showError(info: any): TErrorAction;

  // list all thread posts by slug (e.g. 'global-coverage-castle-nathria')
  setListThreadPostsRequested(param: TListThreadPostsRequestParam): TAction<any>;
  setListThreadPostsSucceeded(results: any): TAction<any>;
  setListThreadPostsFailed(error: any): TAction<any>;

  // peek the top threads by slug (e.g. 'global-coverage-castle-nathria')
  setPeekThreadPostsRequested(threadSlug: string, limit: number, requestId?: number): TAction<any>;
  setPeekThreadPostsSucceeded(results: any, requestId?: number): TAction<any>;
  setPeekThreadPostsFailed(error: any, requestId?: number): TAction<any>;
  setPeekThreadPostsCleared(requestId?: number): TAction<any>;

  // refresh the posts in a thread that is currently being viewed
  setRefreshThreadPostsRequested(threadId: number, lastRefreshTime: string, isAutoRefresh: boolean): TAction<any>;
  setRefreshThreadPostsSucceeded(threadId: number, results: any, isAutoRefresh: boolean): TAction<any>;
  setRefreshThreadPostsFailed(threadId: number, error: any): TAction<any>;

  showNewPosts(): TAction<void>;
};

export const actionTypes = {
  SHOW_LOADING_SPINNER: 'THREADS/SHOW_LOADING_SPINNER',

  SET_LIST_THREAD_POSTS_REQUESTED: 'THREADS/LIST_THREAD_POSTS_REQUESTED',
  SET_LIST_THREAD_POSTS_SUCCEEDED: 'THREADS/LIST_THREAD_POSTS_SUCCEEDED',
  SET_LIST_THREAD_POSTS_FAILED: 'THREADS/LIST_THREAD_POSTS_FAILED',

  SET_PEEK_THREAD_POSTS_REQUESTED: 'THREADS/PEEK_THREAD_POSTS_REQUESTED',
  SET_PEEK_THREAD_POSTS_SUCCEEDED: 'THREADS/PEEK_THREAD_POSTS_SUCCEEDED',
  SET_PEEK_THREAD_POSTS_FAILED: 'THREADS/PEEK_THREAD_POSTS_FAILED',
  SET_PEEK_THREAD_POSTS_CLEARED: 'THREADS/PEEK_THREAD_POSTS_CLEARED',

  SET_REFRESH_THREAD_POSTS_REQUESTED: 'THREADS/REFRESH_THREAD_POSTS_REQUESTED',
  SET_REFRESH_THREAD_POSTS_SUCCEEDED: 'THREADS/REFRESH_THREAD_POSTS_SUCCEEDED',
  SET_REFRESH_THREAD_POSTS_FAILED: 'THREADS/REFRESH_THREAD_POSTS_FAILED',

  SHOW_NEW_POSTS: 'THREADS/SHOW_NEW_POSTS'
};

export const actions: TActions = {
  showLoadingSpinner(show: boolean) {
    return { type: actionTypes.SHOW_LOADING_SPINNER, payload: show };
  },

  showError: createActionSetError,

  //
  // List the posts in a thread
  //
  setListThreadPostsRequested(param: TListThreadPostsRequestParam) {
    return { type: actionTypes.SET_LIST_THREAD_POSTS_REQUESTED, payload: param };
  },

  setListThreadPostsSucceeded(results: any) {
    return { type: actionTypes.SET_LIST_THREAD_POSTS_SUCCEEDED, payload: { results } };
  },

  setListThreadPostsFailed(error: any) {
    return { type: actionTypes.SET_LIST_THREAD_POSTS_FAILED, payload: error };
  },

  //
  // Peek the posts in a thread
  //
  setPeekThreadPostsRequested(threadSlug: string, limit = 3, requestId?: number) {
    return { type: actionTypes.SET_PEEK_THREAD_POSTS_REQUESTED, payload: { slug: threadSlug, limit, requestId } };
  },

  setPeekThreadPostsSucceeded(results: any, requestId?: number) {
    return { type: actionTypes.SET_PEEK_THREAD_POSTS_SUCCEEDED, payload: { results, requestId } };
  },

  setPeekThreadPostsFailed(error: any, requestId?: number) {
    return { type: actionTypes.SET_PEEK_THREAD_POSTS_FAILED, payload: { error, requestId } };
  },

  setPeekThreadPostsCleared(requestId?: number) {
    return { type: actionTypes.SET_PEEK_THREAD_POSTS_CLEARED, payload: { requestId } };
  },

  //
  // Refresh the posts in a thread
  //
  setRefreshThreadPostsRequested(threadId: number, lastRefreshTime: string, isAutoRefresh: boolean) {
    return {
      type: actionTypes.SET_REFRESH_THREAD_POSTS_REQUESTED,
      payload: { threadId, lastRefreshTime, isAutoRefresh }
    };
  },

  setRefreshThreadPostsSucceeded(threadId: number, results: any, isAutoRefresh: boolean) {
    return { type: actionTypes.SET_REFRESH_THREAD_POSTS_SUCCEEDED, payload: { threadId, results, isAutoRefresh } };
  },

  setRefreshThreadPostsFailed(threadId: number, error: any) {
    return { type: actionTypes.SET_REFRESH_THREAD_POSTS_FAILED, payload: { threadId, error } };
  },

  showNewPosts() {
    return { type: actionTypes.SHOW_NEW_POSTS, payload: undefined };
  }
};

//
// Reducer
//

function getInitialState(): TThreadState {
  return {
    listThreadPosts: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    peekThreadPosts: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false,
      requestId: null,
      slug: null,
      limit: null
    },

    refreshThreadPosts: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    // the currently peeked posts
    peekedPosts: [],

    // the active thread being viewed
    thread: null,
    posts: [],
    newPosts: [],
    refreshedAt: '',
    recipients: []
  };
}

export function reducer(state: any = getInitialState(), action: TAction<any>): Partial<TThreadState> {
  switch (action.type) {
    case actionTypes.SHOW_LOADING_SPINNER: {
      return {
        ...state,
        isLoading: action.payload
      };
    }

    case actionTypes.SET_LIST_THREAD_POSTS_REQUESTED: {
      return {
        ...state,
        listThreadPosts: {
          ...state.listThreadPosts,
          isRequested: true,
          isSucceeded: false,
          isErrored: false,
          error: null
        }
      };
    }

    case actionTypes.SET_LIST_THREAD_POSTS_SUCCEEDED: {
      const {
        participantProfiles,
        thread,
        recipients,
        posts,
        refreshed_at: refreshedAt,
        maxPostIdViewed
      } = action.payload.results;

      return {
        ...state,

        listThreadPosts: {
          ...state.listThreadPosts,
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          error: null
        },

        thread: {
          ...state.thread,
          ...thread,
          participantProfiles
        },

        recipients,

        posts,
        maxPostIdViewed,
        refreshedAt
      };
    }

    case actionTypes.SET_LIST_THREAD_POSTS_FAILED: {
      return {
        ...state,
        listThreadPosts: {
          ...state.listThreadPosts,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        },

        thread: null,
        posts: []
      };
    }

    //
    // Peek thread
    //
    case actionTypes.SET_PEEK_THREAD_POSTS_REQUESTED: {
      const { slug, limit, requestId = null } = action.payload;

      return {
        ...state,
        peekThreadPosts: {
          ...state.peekThreadPosts,
          isRequested: true,
          isSucceeded: false,
          isErrored: false,
          error: null,
          requestId,
          slug,
          limit
        }
      };
    }

    case actionTypes.SET_PEEK_THREAD_POSTS_SUCCEEDED: {
      const { requestId } = action.payload;

      if (requestId !== undefined && requestId !== state.peekThreadPosts.requestId) {
        return state;
      }

      const { posts } = action.payload.results;

      return {
        ...state,

        peekThreadPosts: {
          ...state.peekThreadPosts,
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          error: null
        },

        peekedPosts: posts
      };
    }

    case actionTypes.SET_PEEK_THREAD_POSTS_FAILED: {
      const { error, requestId } = action.payload;

      if (requestId !== undefined && requestId !== state.peekThreadPosts.requestId) {
        return state;
      }

      return {
        ...state,
        peekThreadPosts: {
          ...state.peekThreadPosts,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error
        }
      };
    }

    case actionTypes.SET_PEEK_THREAD_POSTS_CLEARED: {
      const { requestId } = action.payload || {};

      if (requestId !== undefined && requestId !== state.peekThreadPosts.requestId) {
        return state;
      }

      return {
        ...state,
        peekThreadPosts: {
          ...state.peekThreadPosts,
          isRequested: false,
          isSucceeded: false,
          isErrored: false,
          error: null,
          requestId: null,
          slug: null,
          limit: null
        },
        peekedPosts: []
      };
    }

    //
    // Refresh thread
    //

    case actionTypes.SET_REFRESH_THREAD_POSTS_REQUESTED: {
      return {
        ...state,
        refreshThreadPosts: {
          isRequested: true,
          isSucceeded: false,
          isErrored: false,
          ...action.payload
        }
      };
    }

    case actionTypes.SET_REFRESH_THREAD_POSTS_SUCCEEDED: {
      const { thread, posts: refreshedPosts, refreshed_at: refreshedAt } = action.payload.results;
      const { isAutoRefresh } = action.payload;

      const newState = {
        ...state,
        refreshThreadPosts: {
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };

      if (state.thread && state.thread.id === thread.id) {
        const currentNewPosts = [...state.newPosts];
        const refreshPostsById = groupByDictUnique(refreshedPosts, 'id');
        const existingPostsById = groupByDictUnique(state.posts, 'id');

        const updatedStatePosts = state.posts.map((post: any) => refreshPostsById[post.id] || post);
        const updatedNewPosts = currentNewPosts.map((post: any) => refreshPostsById[post.id] || post);

        const brandNewPosts = refreshedPosts.filter(
          (post: any) => !existingPostsById[post.id] && !currentNewPosts.some((p) => p.id === post.id)
        );

        if (isAutoRefresh) {
          newState.posts = updatedStatePosts;
          newState.newPosts = [...updatedNewPosts, ...brandNewPosts].filter((post: any) => !post.deleted_at);
        } else {
          newState.posts = [...updatedStatePosts, ...currentNewPosts, ...brandNewPosts];
          newState.newPosts = [];
        }

        newState.refreshedAt = refreshedAt;
      } else if (!state.thread || thread.id !== state.thread.id) {
        // got a refresh for the thread that wasn't loaded so just override it
        newState.thread = thread;
        newState.posts = refreshedPosts;
        newState.refreshedAt = refreshedAt;
      }

      return newState;
    }

    case actionTypes.SET_REFRESH_THREAD_POSTS_FAILED: {
      return {
        ...state,
        refreshThreadPosts: {
          ...state.refreshThreadPosts,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.SHOW_NEW_POSTS: {
      return {
        ...state,
        posts: _.uniqBy([...state.posts, ...state.newPosts], 'id'),
        newPosts: []
      };
    }
  }
  return state;
}

//
// Sagas
//

//
// list thread posts
//
function* listThreadPosts(action: TAction<any>): any {
  const { slug, id } = action.payload;
  try {
    const results = yield call(commonApiServiceAsync.listThreadPosts, { slug, id });
    yield put(messageCenterActions.setMessageCenterThreadRead(id));
    yield put(actions.setListThreadPostsSucceeded(results));
  } catch (e) {
    yield put(actions.setListThreadPostsFailed(e.toString()));
  }
}

function* watchListThreadPostsRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_LIST_THREAD_POSTS_REQUESTED, listThreadPosts);
}

//
// peek thread posts
//
function* peekThreadPosts(action: TAction<any>): any {
  const { slug, limit, requestId } = action.payload;
  try {
    const results = yield call(commonApiServiceAsync.peekThreadPosts, { slug, limit });
    yield put(actions.setPeekThreadPostsSucceeded(results, requestId));
  } catch (e) {
    yield put(actions.setPeekThreadPostsFailed(e.toString(), requestId));
  }
}

function* watchPeekThreadPostsRequested(): any {
  yield takeLatest(actionTypes.SET_PEEK_THREAD_POSTS_REQUESTED, peekThreadPosts);
}

//
// refresh thread posts
//
function* refreshThreadPosts(action: TAction<any>): any {
  const { threadId, lastRefreshTime, isAutoRefresh } = action.payload;
  try {
    const results = yield call(commonApiServiceAsync.refreshThreadPosts, { id: threadId, lastRefreshTime });
    yield put(actions.setRefreshThreadPostsSucceeded(threadId, results, isAutoRefresh));
  } catch (e) {
    yield put(actions.setRefreshThreadPostsFailed(threadId, e.toString()));
  }
}

function* watchRefreshThreadPostsRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_REFRESH_THREAD_POSTS_REQUESTED, refreshThreadPosts);
}

//
// saga exports
//
export const sagas = [
  watchListThreadPostsRequested(),
  watchRefreshThreadPostsRequested(),
  watchPeekThreadPostsRequested()
];
